import {ProjectFeatureFlag} from 'common/report/constants';
import type {CurrentProject} from 'common/report/model-resources/projects';
import {FeatureRollouts} from 'common/report/util/feature-sampling';

import {SupportPlan, SupportPlanTier} from './types';

interface SupportPlanServicePlanIds {
  plan: SupportPlan;
  servicePlanIds: Set<number>;
}

// TODO potentially get these from the same place we specify rollouts
const FEATURES_TO_INCLUDE = [ProjectFeatureFlag.DataTableVirtualized, ProjectFeatureFlag.ValueComparison];

/**
 * The support plan of a ticket depends on the highest service plan
 * associated with the user's account. The mappings of organizations
 * service plan ids to support plans are defined by the support team
 * in this spreadsheet:
 *
 * https://docs.google.com/spreadsheets/d/1j5VXa9pn_-5dOdlF-XHk3Z4MThvIE6pr7XbIcUeQCmU
 */
const RANKED_SUPPORT_PLANS: SupportPlanServicePlanIds[] = [
  {
    plan: SupportPlan.Platinum,
    servicePlanIds: new Set([20, 296, 913]),
  },
  {
    plan: SupportPlan.Gold,
    servicePlanIds: new Set([
      28,
      34,
      455,
      172,
      315,
      83,
      168,
      344,
      6,
      115,
      36,
      203,
      30,
      91,
      26,
      463,
      183,
      388,
      140,
      355,
      100,
      179,
      212,
      164,
      367,
      480,
      380,
      411,
      911,
    ]),
  },
  {
    plan: SupportPlan.Silver,
    servicePlanIds: new Set([
      200,
      216,
      227,
      251,
      252,
      260,
      268,
      283,
      300,
      304,
      319,
      320,
      327,
      340,
      356,
      359,
      372,
      375,
      376,
      379,
      384,
      403,
      407,
      431,
      432,
      439,
      447,
      448,
      451,
      459,
      467,
      468,
      479,
      487,
      488,
      839,
    ]),
  },
  {
    plan: SupportPlan.Custom,
    servicePlanIds: new Set([163, 55, 184, 264, 323]),
  },
  {
    plan: SupportPlan.Standard,
    servicePlanIds: new Set([
      288,
      307,
      284,
      311,
      32,
      88,
      38,
      64,
      231,
      188,
      131,
      60,
      80,
      123,
      152,
      159,
      76,
      4,
      8,
      16,
      24,
      40,
      41,
      44,
      48,
      63,
      72,
      96,
      103,
      112,
      119,
      135,
      139,
      191,
      196,
      208,
      224,
      228,
      256,
      272,
      276,
      279,
      292,
      328,
      332,
      336,
      343,
      352,
      363,
      387,
      399,
      404,
      415,
      419,
      423,
      427,
      436,
      443,
      452,
      472,
      476,
    ]),
  },
];

/**
 * Finds the highest-ranking support plan based on all the service plan ids
 * associated with all the user's organizations by checking if any of the ids
 * belong to one of the pre-defined sets of ids above (non-empty set intersection).
 *
 * If the user has no service plans, their support plan is `Standard`
 * If none of the user's support plans belong to a set, their plan is `Unmapped`
 *
 * Design doc:
 * https://docs.google.com/document/d/13l6VbnMlY4TNNoCgI_zcHKUt-zf7CPcE-rdTTOm_HZw
 */
export function getSupportPlan(accountServicePlanIds: number[]): SupportPlan {
  if (accountServicePlanIds.length === 0) {
    return SupportPlan.Standard;
  }
  for (const {plan, servicePlanIds} of RANKED_SUPPORT_PLANS) {
    for (const id of accountServicePlanIds) {
      if (servicePlanIds.has(id)) {
        return plan;
      }
    }
  }
  return SupportPlan.Unmapped;
}

export function getSupportPlanTier(totalArr: number): SupportPlanTier {
  let tier = SupportPlanTier.Free;
  if (totalArr >= 350_000) {
    tier = SupportPlanTier.Strategic;
  } else if (totalArr >= 100_000) {
    tier = SupportPlanTier.Tier1;
  } else if (totalArr >= 50_000) {
    tier = SupportPlanTier.Tier2;
  } else if (totalArr >= 10_000) {
    tier = SupportPlanTier.Enterprise;
  } else if (totalArr > 0) {
    tier = SupportPlanTier.Growth;
  }
  return tier;
}

export function getSpecialFeaturesForProject(project: CurrentProject): string[] {
  const featureFlagFeatures = FEATURES_TO_INCLUDE.filter((feature) => project?.hasFeatureFlag(feature));
  const rolloutFeatures = Object.values(FeatureRollouts)
    .filter((rollout) => rollout.isEnabled(project))
    .map((rollout) => rollout.name.toString());

  return [...featureFlagFeatures, ...rolloutFeatures];
}
